/**
 * Cohort Analysis Table Component
 *
 * Displays client retention data by cohort month
 */

import React from 'react';
import clsx from 'clsx';
import { Table } from '../../../../UI/V3/Table/Table';
import { TableHeader } from '../../../../UI/V3/Table/Header/TableHeader';
import { TableHeaderCell } from '../../../../UI/V3/Table/HeaderCell/TableHeaderCell';
import { TableBody } from '../../../../UI/V3/Table/Body/TableBody';
import { TableRow } from '../../../../UI/V3/Table/Row/TableRow';
import { TableCell } from '../../../../UI/V3/Table/Cell/TableCell';
import styles from './CohortAnalysisTable.module.scss';
import { CohortData } from '../../Charts/Clients/clientsChartsMockData';

interface CohortAnalysisTableProps {
    title: string;
    data: CohortData[];
}

const getCellBackgroundColor = (value: number): string => {
    // White to #5E8677 gradient based on percentage (75-95%)
    const minValue = 85;
    const maxValue = 95;
    const normalizedValue = Math.min(Math.max(value, minValue), maxValue);
    const ratio = (normalizedValue - minValue) / (maxValue - minValue);

    // RGB for #5E8677
    const targetR = 94;
    const targetG = 134;
    const targetB = 119;

    // RGB for white
    const startR = 255;
    const startG = 255;
    const startB = 255;

    const r = Math.round(startR + (targetR - startR) * ratio);
    const g = Math.round(startG + (targetG - startG) * ratio);
    const b = Math.round(startB + (targetB - startB) * ratio);

    return `rgb(${r}, ${g}, ${b})`;
};

export const CohortAnalysisTable: React.FC<CohortAnalysisTableProps> = ({ title, data }) => {
    return (
        <div className={styles.container}>
            <h3 className={styles.title}>{title}</h3>
            <Table
                tableClassName={styles.table}
                firstColumnFixed={true}
            >
                <TableHeader>
                    <TableHeaderCell>Months</TableHeaderCell>
                    <TableHeaderCell>New clients</TableHeaderCell>
                    <TableHeaderCell>Month 0</TableHeaderCell>
                    <TableHeaderCell>Month 1</TableHeaderCell>
                    <TableHeaderCell>Month 2</TableHeaderCell>
                    <TableHeaderCell>Month 3</TableHeaderCell>
                    <TableHeaderCell>Month 4</TableHeaderCell>
                    <TableHeaderCell>Month 5</TableHeaderCell>
                    <TableHeaderCell>Month 6</TableHeaderCell>
                    <TableHeaderCell>Month 7</TableHeaderCell>
                    <TableHeaderCell>Month 8</TableHeaderCell>
                    <TableHeaderCell>Month 9</TableHeaderCell>
                </TableHeader>
                <TableBody>
                    {data.map((cohort, index) => (
                        <TableRow
                            key={index}
                            className={clsx({ [styles.totalRow]: cohort.isTotal })}
                        >
                            <TableCell
                                className={clsx({ [styles.totalCell]: cohort.isTotal })}
                                style={cohort.isTotal ? { fontWeight: 500 } : undefined}
                            >
                                {cohort.cohortMonth}
                            </TableCell>
                            <TableCell
                                className={clsx({ [styles.totalCell]: cohort.isTotal })}
                                style={cohort.isTotal ? { fontWeight: 500 } : undefined}
                            >
                                {cohort.newClients.toLocaleString()}
                            </TableCell>
                            {cohort.retentionByMonth.map((retention, monthIndex) => (
                                <TableCell
                                    key={monthIndex}
                                    className={clsx(styles.retentionCell, {
                                        [styles.totalCell]: cohort.isTotal,
                                        [styles.highValue]: !cohort.isTotal && retention > 90,
                                        [styles.normalValue]: !cohort.isTotal && retention <= 90
                                    })}
                                    style={
                                        cohort.isTotal
                                            ? { fontWeight: 500 }
                                            : { backgroundColor: getCellBackgroundColor(retention) }
                                    }
                                >
                                    {retention}%
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
};
